import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10; // Increased for production reliability
    this.reconnectDelay = 5000; // Increased to 5 seconds
    this.listeners = new Map();
    this.authToken = null; // Store token for reconnection
  }

  // Connect to WebSocket server
  async connect(token) {
    return new Promise(async (resolve, reject) => {
      // Check if already connected with a valid socket
      if (this.socket && this.socket.connected && this.isConnected) {
        console.log('✅ WebSocket already connected, reusing existing connection');
        resolve(this.socket);
        return;
      }

      // Check if connection is in progress
      if (this.socket && this.socket.connecting) {
        console.log('⏳ WebSocket connection already in progress, waiting...');
        resolve(this.socket);
      return;
    }

    console.log('🔌 Attempting to connect to WebSocket server...');
    console.log('🔌 Token available:', !!token);
    
    if (!token) {
      console.error('❌ No token provided for WebSocket connection');
        reject(new Error('No token provided'));
      return;
    }

    // Store token for potential reconnection
    this.authToken = token;

    // First, check if the backend API is reachable
    const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'https://api.nistylez.com';
    try {
      console.log('🔍 Testing backend connectivity to:', `${websocketUrl}/health`);
      const healthCheck = await fetch(`${websocketUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      }).catch(err => {
        console.warn('⚠️ Health check failed:', err.message);
        return null;
      });
      
      if (healthCheck && healthCheck.ok) {
        const healthData = await healthCheck.json();
        console.log('✅ Backend is reachable:', healthData);
      } else {
        console.warn('⚠️ Backend health check returned non-OK status or failed');
        console.warn('⚠️ Continuing with WebSocket connection attempt anyway...');
      }
    } catch (err) {
      console.warn('⚠️ Could not reach backend API:', err.message);
      console.warn('⚠️ Continuing with WebSocket connection attempt anyway...');
    }
    
    try {
      // Clean up existing socket if any (before creating new one)
      if (this.socket) {
        console.log('🔌 Cleaning up old socket before creating new connection...');
        // Remove all listeners first
        this.socket.removeAllListeners();
        // Then disconnect
        this.socket.disconnect();
        // Clear the reference
        this.socket = null;
        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Use environment variable for WebSocket URL, fallback to production domain
      const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL || 'https://api.nistylez.com';
      const wsPath = process.env.REACT_APP_WEBSOCKET_PATH || '/socket.io/';
      
      console.log('🔌 WebSocket Configuration:');
      console.log('   URL:', websocketUrl);
      console.log('   Path:', wsPath);
      console.log('   Full endpoint:', `${websocketUrl}${wsPath}`);
      console.log('   Token length:', token?.length);
      console.log('   Environment:', process.env.NODE_ENV || 'development');
      
      this.socket = io(websocketUrl, {
        path: wsPath,
        auth: {
          token: token  // Modern Socket.IO way - sent with handshake and maintained throughout session
        },
        transports: ['websocket', 'polling'], // Try websocket first (faster), fallback to polling
        reconnection: false, // Disable auto-reconnection to prevent infinite loops - we'll handle it manually
        timeout: 20000, // Reduced to 20 seconds for faster feedback
        forceNew: true, // Always create new connection to avoid session ID conflicts
        upgrade: true, // Enable protocol upgrades
        rememberUpgrade: false, // Don't remember upgrade to prevent session ID conflicts
        timestampRequests: true, // Add timestamp to prevent caching issues
        autoConnect: true,
        secure: websocketUrl.startsWith('https'), // Enable secure connection for HTTPS
        withCredentials: true, // Include credentials for cross-origin requests
        rejectUnauthorized: false, // Allow self-signed certificates (production should use valid certs)
        extraHeaders: {
          'Authorization': `Bearer ${token}`
        }
      });

      this.setupEventListeners();
      
      // Add connection success handler
      this.socket.on('connect', () => {
        console.log('✅ Customer WebSocket connected successfully');
        console.log('🔌 Customer socket ID:', this.socket.id);
        console.log('🔌 Connected to:', websocketUrl);
        console.log('🔌 Socket transport:', this.socket.io.engine.transport.name);
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connection_established', { 
          socketId: this.socket.id,
          timestamp: new Date().toISOString(),
          url: websocketUrl,
          transport: this.socket.io.engine.transport.name
        });
        resolve(); // ✅ Resolve promise on successful connection
      });

      // Listen for connection established event from server (like provider)
      this.socket.on('connection_established', (data) => {
        console.log('🔌 Customer WebSocket connection established:', data);
        this.emit('connection_established', data);
      });
      
      // Add connection error handler
      this.socket.on('connect_error', (error) => {
        console.error('❌ Customer WebSocket connection error:', error);
        console.error('❌ Error details:', {
          message: error.message,
          type: error.type,
          description: error.description,
          url: websocketUrl,
          path: wsPath,
          context: error.context
        });
        
        // Log diagnostic information
        console.error('🔍 Diagnostic Information:');
        console.error('   - Check if backend is running at:', websocketUrl);
        console.error('   - Expected Socket.IO path:', wsPath);
        console.error('   - Full Socket.IO URL:', `${websocketUrl}${wsPath}`);
        console.error('   - Transport attempted:', this.socket?.io?.engine?.transport?.name || 'unknown');
        console.error('   - CORS may be blocking the connection if origin is not whitelisted');
        console.error('   - Verify ALLOWED_ORIGINS includes:', window.location.origin);
        
        this.isConnected = false;
        
        // Provide user-friendly error messages
        let errorMessage = 'Connection failed';
        let userMessage = '';
        
        if (error.message.includes('xhr poll error') || error.message.includes('polling')) {
          errorMessage = 'WebSocket server is not available';
          userMessage = 'The chat server is currently unavailable. Your messages will be sent via API only.';
          console.error('💡 Troubleshooting: The backend Socket.IO server may not be running or is not accessible.');
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Connection timeout';
          userMessage = 'Connection to chat server timed out. Please check your internet connection. Messages will be sent via API.';
          console.error('💡 Troubleshooting: The server is taking too long to respond. Check network connectivity and server load.');
        } else if (error.message.includes('authentication') || error.message.includes('unauthorized') || error.message.includes('Authentication required')) {
          errorMessage = 'Authentication failed';
          userMessage = 'Session expired. Please refresh the page to reconnect to chat.';
          console.error('💡 Troubleshooting: Token may be invalid, expired, or not properly sent to the server.');
        } else if (error.message.includes('CORS')) {
          errorMessage = 'CORS error';
          userMessage = 'Connection blocked by server security settings. Please contact support.';
          console.error('💡 Troubleshooting: The server CORS policy is blocking this origin:', window.location.origin);
        } else {
          errorMessage = error.message || 'Unknown connection error';
          userMessage = 'Unable to connect to real-time chat. Messages will be sent via API.';
          console.error('💡 Troubleshooting: Unexpected error. Check browser console and server logs.');
        }
        
        console.warn(`⚠️ ${errorMessage}: ${userMessage}`);
        error.userMessage = userMessage;
        
        this.emit('connection_error', error);
        
        // ✅ Resolve instead of letting error propagate - graceful degradation
        console.log('💬 Continuing without WebSocket - messages will use API only');
        resolve(); // Allow app to continue with API-only mode
      });
      
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      this.isConnected = false;
        // Resolve even on error - allow app to continue
        console.log('💬 Exception during connection - continuing with API only');
        resolve();
    }
    });
  }

  // Setup event listeners
  setupEventListeners() {
    if (!this.socket) return;

    // Connection established
    this.socket.on('connection_established', (data) => {
      console.log('WebSocket connection established:', data);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connection_established', data);
    });

    // Connection error
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.isConnected = false;
      this.emit('connection_error', error);
    });

    // Disconnected
    this.socket.on('disconnect', (reason) => {
      console.log('❌ Customer WebSocket disconnected:', reason);
      this.isConnected = false;
      this.emit('disconnected', 1000, reason);
      
      // Note: Socket.IO handles reconnection automatically now since we enabled reconnection: true
    });

    // Reconnection attempt
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Customer WebSocket reconnection attempt ${attemptNumber}`);
      this.reconnectAttempts = attemptNumber;
    });

    // Reconnection successful
    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ Customer WebSocket reconnected after ${attemptNumber} attempts`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connection_established', { 
        socketId: this.socket.id,
        timestamp: new Date().toISOString(),
        reconnected: true
      });
    });

    // Reconnection failed
    this.socket.on('reconnect_failed', () => {
      console.log('❌ Customer WebSocket reconnection failed');
      this.isConnected = false;
    });

    // New message received
    this.socket.on('new_message', (messageData) => {
      console.log('🔔 [WebSocketService] new_message event received from backend:', messageData);
      console.log('🔔 [WebSocketService] Emitting to listeners...');
      this.emit('new_message', messageData);
      console.log('🔔 [WebSocketService] Event emitted to', this.listeners.get('new_message')?.length || 0, 'listeners');
    });

    // Message sent confirmation
    this.socket.on('message_sent', (messageData) => {
      console.log('Message sent confirmation:', messageData);
      this.emit('message_sent', messageData);
    });

    // Typing status
    this.socket.on('typing_status', (data) => {
      console.log('Typing status received:', data);
      this.emit('typing_status', data);
    });

    // Chat error
    this.socket.on('chat_error', (error) => {
      console.error('Chat error:', error);
      this.emit('chat_error', error);
    });

    // Joined chat room
    this.socket.on('joined_chat', (data) => {
      console.log('Joined chat room:', data);
      this.emit('joined_chat', data);
    });

    // Left chat room
    this.socket.on('left_chat', (data) => {
      console.log('Left chat room:', data);
      this.emit('left_chat', data);
    });

    // Ping/Pong
    this.socket.on('pong', (data) => {
      console.log('Pong received:', data);
    });
  }

  // Send chat message
  sendChatMessage(chatId, content, messageType = 'text') {
    if (!this.socket || !this.isConnected) {
      console.error('WebSocket not connected');
      return false;
    }

    console.log('Sending chat message via WebSocket:', { chatId, content, messageType });
    this.socket.emit('chat_message', {
      chatId,
      content,
      messageType
    });
    return true;
  }

  // Send typing status
  sendTypingStatus(chatId, isTyping) {
    if (!this.socket || !this.isConnected) {
      return false;
    }

    this.socket.emit('typing', {
      chatId,
      isTyping
    });
    return true;
  }

  // Join chat room
  joinChat(chatId) {
    if (!this.socket || !this.isConnected) {
      return false;
    }

    this.socket.emit('join_chat', {
      chatId
    });
    return true;
  }

  // Leave chat room
  leaveChat(chatId) {
    if (!this.socket || !this.isConnected) {
      return false;
    }

    this.socket.emit('leave_chat', { chatId });
    return true;
  }

  // Send ping
  ping() {
    if (!this.socket || !this.isConnected) {
      return false;
    }

    this.socket.emit('ping');
    return true;
  }

  // Add event listener
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Remove event listener
  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  // Add event listener (alias for backward compatibility)
  addEventListener(event, callback) {
    this.on(event, callback);
  }

  // Remove event listener (alias for backward compatibility)
  removeEventListener(event, callback) {
    this.off(event, callback);
  }

  // Emit event to listeners
  emit(event, data) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  // Disconnect
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket...');
      // Remove all listeners before disconnecting
      if (this.socket.connected) {
        this.socket.removeAllListeners();
      }
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.reconnectAttempts = 0;
      console.log('✅ WebSocket disconnected and cleaned up');
    }
  }

  // Soft disconnect - doesn't clear listeners (for page navigation)
  softDisconnect() {
    if (this.socket && this.socket.connected) {
      console.log('🔌 Soft disconnect - keeping connection alive for reuse');
      // Don't actually disconnect, just mark as not actively connected
      // The socket will stay alive and can be reused
      this.isConnected = this.socket.connected;
    }
  }

  // Get connection status (detailed)
  getConnectionStatusDetailed() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket?.id
    };
  }

  // Additional methods for GemmaChatbot compatibility
  markMessageAsRead(messageId) {
    // Placeholder for message read functionality
    console.log('Marking message as read:', messageId);
  }

  joinChatRoom(roomId) {
    if (!this.socket || !this.isConnected) {
      return false;
    }
    this.socket.join(`chat_${roomId}`);
    return true;
  }

  leaveChatRoom() {
    if (!this.socket || !this.isConnected) {
      return false;
    }
    // Leave all chat rooms
    this.socket.leaveAll();
    return true;
  }

  // Check if connected (like provider)
  getConnectionStatus() {
    return this.isConnected === true && this.socket?.connected === true;
  }

  requestHumanAgent(reason) {
    if (!this.socket || !this.isConnected) {
      return false;
    }
    this.socket.emit('request_human_agent', { reason });
    return true;
  }

  sendChatMessage(message, messageType = 'text', metadata = {}) {
    if (!this.socket || !this.isConnected) {
      return false;
    }
    this.socket.emit('chat_message', {
      content: message,
      messageType,
      metadata
    });
    return true;
  }

  sendTypingIndicator(isTyping) {
    if (!this.socket || !this.isConnected) {
      return false;
    }
    this.socket.emit('typing', { isTyping });
    return true;
  }

  // Subscribe to provider slot updates
  subscribeToProviderSlots(providerId, date) {
    if (!this.socket || !this.isConnected) {
      console.warn('WebSocket not connected, cannot subscribe to provider slots');
      return false;
    }
    
    this.socket.emit('subscribe_provider_slots', {
      providerId,
      date
    });
    return true;
  }

  // Unsubscribe from provider slot updates
  unsubscribeFromProviderSlots(providerId, date) {
    if (!this.socket || !this.isConnected) {
      console.warn('WebSocket not connected, cannot unsubscribe from provider slots');
      return false;
    }
    
    this.socket.emit('unsubscribe_provider_slots', {
      providerId,
      date
    });
    return true;
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export default websocketService;